import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useOrg } from '../data/orgs';
import { useVisibleDeals, useSuperadmin } from '../data/permissions';
import Icon from './Icons';
import { openGuide } from './GuidePanel';

// What you can create, from anywhere. Project-scoped kinds deep-link into the
// current project's Documents tab (?draft= opens Draft-with-AI preset to the
// kind, ?add=1 opens the add-document portal); outside a project a second
// step asks which project.
const CREATE_KINDS = [
  { key: 'document', label: 'Document (AI draft)', icon: 'doc', param: 'draft=document' },
  { key: 'deck', label: 'Presentation', icon: 'deck', param: 'draft=deck' },
  { key: 'workbook', label: 'Workbook (Excel)', icon: 'sheet', param: 'draft=workbook' },
  { key: 'upload', label: 'Upload a file', icon: 'upload', param: 'add=1' },
];

// Cross-project library views — the artifact kinds, one click from anywhere.
const LIBRARY_LINKS = [
  { kind: 'documents', label: 'Documents', icon: 'doc' },
  { kind: 'presentations', label: 'Presentations', icon: 'deck' },
  { kind: 'workbooks', label: 'Workbooks', icon: 'sheet' },
];

// Recently-opened deals, tracked per org in localStorage (most recent first).
const recentKey = (orgId) => `fl-recent-deals:${orgId}`;

export function recordRecentDeal(orgId, dealId) {
  try {
    const list = JSON.parse(localStorage.getItem(recentKey(orgId)) || '[]').filter((id) => id !== dealId);
    list.unshift(dealId);
    localStorage.setItem(recentKey(orgId), JSON.stringify(list.slice(0, 12)));
  } catch { /* private mode etc. — recency is a nicety */ }
}

function readRecents(orgId) {
  try {
    return JSON.parse(localStorage.getItem(recentKey(orgId)) || '[]');
  } catch {
    return [];
  }
}

const STATUS_DOT = {
  draft: 'var(--muted)',
  negotiating: 'var(--info)',
  agreed: 'var(--ok)',
  executed: 'var(--ok)',
  closed: 'var(--muted)',
};

// The left rail: icon navigation + recent projects. Collapsible to a slim
// dot rail (state persists). Desktop-only (mobile keeps the top nav; the
// rail hides under 900px — see index.css). Real features only — no dead
// links: items that need a role (Activity, Team, Docs) render per role.
export default function Sidebar() {
  const { orgId, role } = useOrg();
  const superadmin = useSuperadmin();
  const { dealId: activeDealId } = useParams();
  const { deals } = useVisibleDeals();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('fl-sidebar-collapsed') === '1'; } catch { return false; }
  });
  // Create flyout: open/closed + the kind awaiting a project choice (when
  // clicked outside a project).
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingKind, setPendingKind] = useState(null);

  // Navigating anywhere closes the flyout.
  useEffect(() => {
    setCreateOpen(false);
    setPendingKind(null);
  }, [location.pathname, location.search]);

  if (!orgId) return null;

  const toggle = () =>
    setCollapsed((c) => {
      try { localStorage.setItem('fl-sidebar-collapsed', c ? '0' : '1'); } catch { /* nicety */ }
      return !c;
    });

  // Sidebar renders on the shell route (/*), so the active deal comes from
  // the pathname, not useParams.
  const inDealId = activeDealId || (location.pathname.match(/^\/deals\/([^/]+)/) || [])[1] || '';
  // Active library kind ('' = All) when on /library, else null.
  const libKind = location.pathname === '/library'
    ? (new URLSearchParams(location.search).get('kind') || '')
    : null;

  const recents = readRecents(orgId);
  const rank = new Map(recents.map((id, i) => [id, i]));
  const ordered = [...(deals || [])].sort((a, b) => {
    const ra = rank.has(a.id) ? rank.get(a.id) : Infinity;
    const rb = rank.has(b.id) ? rank.get(b.id) : Infinity;
    if (ra !== rb) return ra - rb;
    return (b.createdAt || 0) - (a.createdAt || 0);
  });

  return (
    <aside className={`cs-sidebar${collapsed ? ' collapsed' : ''}`} aria-label="Projects">
      <button
        type="button"
        className="cs-sidebar-toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? '»' : '«'}
      </button>

      {collapsed ? (
        <div className="cs-sidebar-list" style={{ alignItems: 'center' }}>
          {ordered.slice(0, 10).map((d) => (
            <NavLink
              key={d.id}
              to={`/deals/${d.id}`}
              className={`cs-sidebar-deal ${d.id === activeDealId ? 'active' : ''}`}
              title={d.name}
            >
              <span className="cs-sidebar-dot" style={{ background: STATUS_DOT[d.status] || 'var(--muted)' }} />
            </NavLink>
          ))}
        </div>
      ) : (
        <>
          <nav className="cs-sidebar-nav">
            <NavLink to="/" end className="cs-sidebar-link">
              <Icon name="home" /> Home
            </NavLink>
            <button
              type="button"
              className="cs-sidebar-link"
              onClick={() => { setCreateOpen((o) => !o); setPendingKind(null); }}
              aria-expanded={createOpen}
            >
              <Icon name="plus" /> Create
              <span style={{ marginLeft: 'auto', display: 'inline-flex', transform: createOpen ? 'rotate(180deg)' : 'none', transition: 'transform var(--t-fast, 120ms)' }}>
                <Icon name="chevronDown" size={13} />
              </span>
            </button>
            {createOpen && !pendingKind && (
              <div className="u-stack" style={{ '--gap': '1px' }}>
                <button
                  type="button"
                  className="cs-sidebar-link"
                  style={{ paddingLeft: 'var(--sp-6)' }}
                  onClick={() => navigate('/projects?new=1')}
                >
                  <Icon name="folder" size={15} /> New project
                </button>
                {CREATE_KINDS.map((k) => (
                  <button
                    key={k.key}
                    type="button"
                    className="cs-sidebar-link"
                    style={{ paddingLeft: 'var(--sp-6)' }}
                    onClick={() => {
                      if (inDealId) navigate(`/deals/${inDealId}?tab=documents&${k.param}`);
                      else setPendingKind(k);
                    }}
                  >
                    <Icon name={k.icon} size={15} /> {k.label}
                  </button>
                ))}
              </div>
            )}
            {createOpen && pendingKind && (
              <div className="u-stack" style={{ '--gap': '1px' }}>
                <div className="cs-sidebar-head" style={{ paddingTop: 'var(--sp-1)' }}>
                  {pendingKind.label} — in which project?
                </div>
                {ordered.slice(0, 6).map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className="cs-sidebar-link"
                    style={{ paddingLeft: 'var(--sp-6)' }}
                    title={d.name}
                    onClick={() => navigate(`/deals/${d.id}?tab=documents&${pendingKind.param}`)}
                  >
                    <span className="cs-sidebar-dot" style={{ background: STATUS_DOT[d.status] || 'var(--muted)', flexShrink: 0 }} />
                    <span className="cs-sidebar-deal-name">{d.name}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className="cs-sidebar-link"
                  style={{ paddingLeft: 'var(--sp-6)' }}
                  onClick={() => navigate('/projects?new=1')}
                >
                  <Icon name="plus" size={15} /> New project…
                </button>
              </div>
            )}
            <NavLink to="/projects" className="cs-sidebar-link">
              <Icon name="folder" /> Projects
            </NavLink>
            <NavLink to="/sam" className="cs-sidebar-link">
              <Icon name="search" /> SAM desk
            </NavLink>
            <NavLink to="/knowledge" className="cs-sidebar-link">
              <Icon name="book" /> Knowledge
            </NavLink>
            {superadmin && (
              <NavLink to="/jobs" className="cs-sidebar-link">
                <Icon name="activity" /> Jobs
              </NavLink>
            )}
            {(role === 'admin' || role === 'owner') && (
              <>
                <NavLink to="/activity" className="cs-sidebar-link">
                  <Icon name="eye" /> Activity
                </NavLink>
                <NavLink to="/team" className="cs-sidebar-link">
                  <Icon name="users" /> People &amp; Access
                </NavLink>
                <NavLink to="/admin/docs" className="cs-sidebar-link">
                  <Icon name="book" /> Docs
                </NavLink>
                <NavLink to="/pricing" className="cs-sidebar-link">
                  <Icon name="card" /> Billing
                </NavLink>
              </>
            )}
            {superadmin && (
              <>
                <NavLink to="/brain" className="cs-sidebar-link">
                  <Icon name="brain" /> The Brain
                </NavLink>
                <NavLink to="/support" className="cs-sidebar-link">
                  <Icon name="eye" /> Support console
                </NavLink>
              </>
            )}
            <button
              type="button"
              className="cs-sidebar-link"
              onClick={() => window.dispatchEvent(new CustomEvent('fl:account'))}
            >
              <Icon name="gear" /> Settings
            </button>
            <button type="button" className="cs-sidebar-link" onClick={() => openGuide()}>
              <Icon name="help" /> Help
            </button>
          </nav>

          {/* Cross-project library — browse every artifact by kind. */}
          <div className="cs-sidebar-head">Library</div>
          <nav className="cs-sidebar-nav" aria-label="Library">
            <Link to="/library" className={`cs-sidebar-link${libKind === '' ? ' active' : ''}`}>
              <Icon name="list" /> All artifacts
            </Link>
            {LIBRARY_LINKS.map((l) => (
              <Link
                key={l.kind}
                to={`/library?kind=${l.kind}`}
                className={`cs-sidebar-link${libKind === l.kind ? ' active' : ''}`}
              >
                <Icon name={l.icon} /> {l.label}
              </Link>
            ))}
          </nav>

          <div className="cs-sidebar-head">Recent projects</div>
          <div className="cs-sidebar-list">
            {deals === null && <div className="cs-skeleton" style={{ height: 60, margin: 'var(--sp-2)' }} />}
            {deals && !deals.length && (
              <div className="u-xs u-muted" style={{ padding: 'var(--sp-3)' }}>
                No projects yet — create one from Projects.
              </div>
            )}
            {ordered.slice(0, 10).map((d) => (
              <NavLink
                key={d.id}
                to={`/deals/${d.id}`}
                className={`cs-sidebar-deal ${d.id === activeDealId ? 'active' : ''}`}
              >
                <span className="cs-sidebar-dot" style={{ background: STATUS_DOT[d.status] || 'var(--muted)' }} />
                <span className="cs-sidebar-deal-name">{d.name}</span>
                <span className="u-xs u-muted u-tabular">{d.docCount || 0}</span>
              </NavLink>
            ))}
          </div>
        </>
      )}
    </aside>
  );
}
